import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Star } from "@phosphor-icons/react";

const ICON_OPTIONS = ["DeviceMobile", "WifiHigh", "SimCard", "Phone", "House", "GlobeHemisphereEast"];

export default function AdminPlans() {
  const [cats, setCats] = useState([]);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/telecom/service-plans/admin").then(({ data }) => {
      setCats(data.categories || []);
      setPlans(data.plans || []);
    });
  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, []);

  const catName = (key) => cats.find((c) => c.key === key)?.name || key;

  const blank = (category) => ({
    id: null, category: category || cats[0]?.key || "mobile", name: "", nickname: "", headline: "",
    price_dollars: "", features_text: "", accent: "#10B981", icon: "Phone", popular: false, active: true, cta: "", sort_order: 0,
  });
  const edit = (p) => setForm({
    ...p, price_dollars: (p.price_cents / 100).toFixed(2), features_text: (p.features || []).join("\n"),
  });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      category: form.category, name: form.name.trim(), nickname: form.nickname || "", headline: form.headline || "",
      price_cents: Math.round(Number(form.price_dollars || 0) * 100),
      features: (form.features_text || "").split("\n").map((s) => s.trim()).filter(Boolean),
      accent: form.accent || "#10B981", icon: form.icon || "Phone", popular: !!form.popular, active: !!form.active,
      cta: form.cta || "", sort_order: Number(form.sort_order || 0),
    };
    setSaving(true);
    try {
      if (form.id) await api.patch(`/telecom/service-plans/admin/${form.id}`, payload);
      else await api.post("/telecom/service-plans/admin", payload);
      setForm(null); await load(); toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/telecom/service-plans/admin/${p.id}`); await load(); toast.success("Deleted"); } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (p, field) => {
    try { await api.patch(`/telecom/service-plans/admin/${p.id}`, { [field]: !p[field] }); await load(); } catch (e) { toast.error(apiError(e)); }
  };

  const byCat = useMemo(() => {
    const groups = {};
    for (const c of cats) groups[c.key] = [];
    for (const p of plans) { (groups[p.category] = groups[p.category] || []).push(p); }
    return groups;
  }, [cats, plans]);

  return (
    <div className="mx-auto max-w-5xl space-y-8" data-testid="admin-plans">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Plans Manager</h1>
          <p className="mt-1 text-sm text-muted-foreground">Create and price <span className="font-semibold">Mobile Wireless</span> and <span className="font-semibold">Home &amp; Landline</span> plans. Everything here appears on the public <span className="font-semibold">Plans</span> page and checkout.</p>
        </div>
        <button onClick={() => setForm(blank())} data-testid="add-plan" className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"><Plus size={16} weight="bold" /> Add plan</button>
      </div>

      {form && (
        <div className="space-y-3 rounded-xl border border-primary/40 bg-primary/5 p-4" data-testid="plan-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Category</label>
              <select data-testid="plan-category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
                {cats.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Name</label>
              <input data-testid="plan-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Nickname</label>
              <input value={form.nickname} onChange={(e) => setForm((f) => ({ ...f, nickname: e.target.value }))} placeholder="e.g. The everyday favorite" className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Price (USD / month)</label>
              <input data-testid="plan-price" type="number" step="0.01" value={form.price_dollars} onChange={(e) => setForm((f) => ({ ...f, price_dollars: e.target.value }))} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-muted-foreground">Headline (shown in quotes)</label>
              <input value={form.headline} onChange={(e) => setForm((f) => ({ ...f, headline: e.target.value }))} placeholder="e.g. More data, more freedom." className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-muted-foreground">Features (one per line)</label>
              <textarea data-testid="plan-features" value={form.features_text} onChange={(e) => setForm((f) => ({ ...f, features_text: e.target.value }))} rows={5} placeholder={"25 GB high-speed data\nUnlimited talk & text"} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Icon</label>
              <select value={form.icon} onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
                {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Accent color</label>
              <div className="mt-1 flex items-center gap-2">
                <input type="color" value={form.accent} onChange={(e) => setForm((f) => ({ ...f, accent: e.target.value }))} className="h-9 w-12 rounded border border-input bg-background" />
                <input value={form.accent} onChange={(e) => setForm((f) => ({ ...f, accent: e.target.value }))} className="block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">CTA label (optional)</label>
              <input value={form.cta} onChange={(e) => setForm((f) => ({ ...f, cta: e.target.value }))} placeholder="e.g. Get Plus" className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Sort order</label>
              <input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} /> Visible on Plans page</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.popular} onChange={(e) => setForm((f) => ({ ...f, popular: e.target.checked }))} /> Mark as “Most Popular”</label>
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} data-testid="save-plan" className="rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground disabled:opacity-60">{saving ? "Saving…" : "Save plan"}</button>
            <button onClick={() => setForm(null)} className="rounded-full border border-border px-5 py-2 text-sm font-semibold">Cancel</button>
          </div>
        </div>
      )}

      {cats.map((c) => (
        <section key={c.key} className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-semibold">{c.name} <span className="text-sm font-normal text-muted-foreground">· {(byCat[c.key] || []).length} plan(s)</span></h2>
            <button onClick={() => setForm(blank(c.key))} className="text-xs font-semibold text-primary">+ Add to {c.name}</button>
          </div>
          <div className="space-y-2">
            {(byCat[c.key] || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((p) => (
              <div key={p.id} data-testid={`admin-plan-${p.key}`} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2">
                <span className="h-8 w-8 shrink-0 rounded-md" style={{ background: (p.accent || "#10B981") + "22", border: `1px solid ${p.accent || "#10B981"}` }} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{p.name} {p.popular && <span className="text-xs font-semibold text-primary">★ Popular</span>} {!p.active && <span className="text-xs text-muted-foreground">(hidden)</span>}</p>
                  <p className="text-xs text-muted-foreground">${(p.price_cents / 100).toFixed(2)}/mo · {(p.features || []).length} features · <span className="font-mono">{p.key}</span></p>
                </div>
                <button onClick={() => toggle(p, "popular")} title="Toggle popular" data-testid={`popular-${p.key}`} className={p.popular ? "text-primary" : "text-muted-foreground hover:text-foreground"}><Star size={16} weight={p.popular ? "fill" : "regular"} /></button>
                <button onClick={() => toggle(p, "active")} className="text-xs">{p.active ? "Hide" : "Show"}</button>
                <button onClick={() => edit(p)} className="text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>
                <button onClick={() => del(p)} className="text-red-400 hover:text-red-500"><Trash size={16} /></button>
              </div>
            ))}
            {(byCat[c.key] || []).length === 0 && <p className="text-sm text-muted-foreground">No plans in this category yet.</p>}
          </div>
        </section>
      ))}
    </div>
  );
}
